import { notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/nextjs";
import { type ClassValue, clsx } from "clsx";
import Fuse, { type FuseResult } from "fuse.js";
import pLimit from "p-limit";
import { twMerge } from "tailwind-merge";
import type { z } from "zod";
import { isProduction, isSandbox } from "@/lib/environment";
import { time } from "@/lib/time";
import type { createLogger } from "./logger";

/**
 * Returns the configured Vibrant super organization identifier or undefined when missing.
 */
export function getSafeVibrantSuperOrgId(): string | undefined {
  const rawValue = process.env.NEXT_PUBLIC_VIBRANT_SUPER_ORG_ID;
  if (typeof rawValue !== "string") {
    return undefined;
  }

  const trimmedValue = rawValue.trim();
  if (trimmedValue.length === 0) {
    return undefined;
  }

  return trimmedValue;
}

/**
 * Returns the configured Vibrant super organization identifier or throws when it is missing.
 */
export function getVibrantSuperOrgId(): string {
  const vibrantSuperOrgId = getSafeVibrantSuperOrgId();
  if (!vibrantSuperOrgId) {
    throw new Error("NEXT_PUBLIC_VIBRANT_SUPER_ORG_ID must be configured in every environment.");
  }

  return vibrantSuperOrgId;
}

/**
 * A type that allows all properties of a given type to be null.
 */
export type NullableProps<T> = { [K in keyof T]: T[K] | null };

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const LOCAL_BASE_URL = "http://localhost:3000";
export const SANDBOX_BASE_URL = "https://sandbox.ultralighthealth.com";
export const PRODUCTION_BASE_URL = "https://app.ultralighthealth.com";

/**
 * Gets the base URL for the application based on the environment.
 * Defaults to process.env.NEXT_PUBLIC_SITE_URL if set, otherwise uses
 * https://app.ultralighthealth.com for production,
 * https://sandbox.ultralighthealth.com for sandbox,
 * http://localhost:3000 for local.
 *
 * @returns The base URL for the application.
 */
export function getBaseUrl(): string {
  if (process.env.NEXT_PUBLIC_SITE_URL) {
    return process.env.NEXT_PUBLIC_SITE_URL;
  }

  if (isProduction()) {
    return PRODUCTION_BASE_URL;
  }

  if (isSandbox()) {
    return SANDBOX_BASE_URL;
  }

  return LOCAL_BASE_URL;
}

/**
 * Gets the absolute URL for an asset based on the environment.
 * Uses https://app.ultralighthealth.com for production,
 * https://sandbox.ultralighthealth.com for sandbox,
 * and http://localhost:3000 for local.
 *
 * @param path - The relative path of the asset (should start with /).
 * @returns The absolute URL for the asset.
 */
export function getAssetUrl(path: string): string {
  return `${getBaseUrl()}${path}`;
}

/**
 * Formats a date string into a human-readable format.
 *
 * If the date is yesterday, today or tomorrow, it will use the relative time.
 * Uses the MM/dd format for all dates in the same year, except for yesterday, today and tomorrow.
 * Uses the MM/dd/YYYY for all others.
 *
 * @param dateString The date string to format.
 * @returns The formatted date string.
 */
export function formatDate(dateString: string): string {
  const date = time(dateString);
  if (!date.isValid()) return "Unknown";

  const today = time();
  const yesterday = today.subtract(1, "day");
  const tomorrow = today.add(1, "day");

  if (date.isSame(today, "day")) return "Today";
  if (date.isSame(tomorrow, "day")) return "Tomorrow";
  if (date.isSame(yesterday, "day")) return "Yesterday";
  if (date.isSame(today, "year")) return date.format("MM/DD");

  return date.format("MM/DD/YYYY");
}

/**
 * Formats a time string into a human-readable format.
 *
 * @param timeString The time string to format.
 * @returns The formatted time string.
 */
export function formatTime(timeString: string): string {
  const t = time(timeString);
  if (!t.isValid()) return "";
  return t.format("h:mm A");
}

/**
 * Formats a date string as `formatDate` + `formatTime`: "Today 7:43 AM", "Yesterday 6:05 PM",
 * "09/12 6:05 PM", "01/02/2025 4:03 PM" (year only outside the current year).
 *
 * @param dateString The date string to format.
 * @returns The formatted date and time.
 */
export function formatDateAndTime(dateString: string): string {
  return `${formatDate(dateString)} ${formatTime(dateString)}`;
}

/**
 * Formats a date string with time into a human-readable format.
 * Example: "Jul 16, 2025 12:36 PM"
 *
 * @param dateString The date string to format.
 * @returns The formatted date and time string.
 */
export function formatDateTime(dateString: string): string {
  const t = time(dateString);
  if (!t.isValid()) return "";
  return t.format("MMM D, YYYY h:mm A");
}

export function formatRelativeTime(dateString: string): string {
  const date = time(dateString);
  const today = time();

  if (date.isSame(today, "day")) {
    return formatTime(dateString);
  }
  return formatDate(dateString);
}

/**
 * Returns a function that validates a form field against a Zod schema.
 *
 * @param schema The Zod schema to validate the field against.
 * @returns A function that validates the field against the schema.
 */
export function fieldValidator(schema: z.ZodType<any>) {
  return (value: string | null | undefined): string | undefined => {
    const result = schema.safeParse(value);
    return result.error?.issues[0]?.message || undefined;
  };
}

export interface DownloadFileRequest {
  url: string;
  /**
   * Max time to wait for the download request to complete.
   */
  timeoutMs?: number;
  /**
   * Maximum allowed bytes to download (best-effort; enforced while reading the response body).
   */
  maxBytes?: number;
  /**
   * If true, rejects any URL that is not https://
   */
  requireHttps?: boolean;
  /**
   * If provided, validates the response Content-Type header starts with this prefix.
   * Example: "application/pdf"
   *
   * If the Content-Type header is missing or blank, this throws.
   */
  expectedContentTypePrefix?: string;
  /**
   * If true, validates that the downloaded bytes begin with a PDF header ("%PDF-").
   */
  validatePdfHeader?: boolean;
}

export interface DownloadFileResult {
  contentType: string | undefined;
  bytes: Uint8Array;
}

function isPdfHeader(bytes: Uint8Array): boolean {
  // "%PDF-" magic bytes
  return (
    bytes.length >= 5 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d
  );
}

async function readResponseBytesWithLimit({
  response,
  maxBytes,
}: {
  response: Response;
  maxBytes: number;
}): Promise<Uint8Array> {
  const body = response.body as unknown as { getReader?: () => any } | null;
  const reader = body?.getReader ? body.getReader() : undefined;

  if (!reader) {
    const arrayBuffer = await response.arrayBuffer();
    const bytes =
      arrayBuffer instanceof ArrayBuffer
        ? new Uint8Array(arrayBuffer)
        : new Uint8Array(arrayBuffer as unknown as ArrayLike<number>);

    if (bytes.byteLength > maxBytes) {
      throw new Error(`Downloaded file exceeded max size (${bytes.byteLength} > ${maxBytes}).`);
    }
    return bytes;
  }

  const chunks: Uint8Array[] = [];
  let totalBytes = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;

    const chunk = value instanceof Uint8Array ? value : new Uint8Array(value);
    totalBytes += chunk.byteLength;

    if (totalBytes > maxBytes) {
      throw new Error(`Downloaded file exceeded max size (${totalBytes} > ${maxBytes}).`);
    }

    chunks.push(chunk);
  }

  const result = new Uint8Array(totalBytes);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return result;
}

/**
 * Downloads a file from a URL.
 *
 * Note: This helper is used in server environments (e.g., webhooks/background jobs). Be cautious
 * about using it with untrusted URLs.
 */
export async function downloadFile({
  url,
  timeoutMs = 30_000,
  maxBytes = 25 * 1024 * 1024,
  requireHttps = false,
  expectedContentTypePrefix,
  validatePdfHeader = false,
}: DownloadFileRequest): Promise<DownloadFileResult> {
  const parsedUrl = new URL(url);

  if (requireHttps && parsedUrl.protocol !== "https:") {
    throw new Error("downloadFile only supports https:// URLs when requireHttps is enabled.");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      throw new Error(`Failed to download file (status=${response.status}).`);
    }

    const contentType = response.headers.get("Content-Type") ?? undefined;

    if (expectedContentTypePrefix) {
      if (!contentType) {
        throw new Error(
          `Missing Content-Type header for downloaded file (expected prefix: ${expectedContentTypePrefix})`,
        );
      }

      const normalized = contentType.trim().toLowerCase();
      const expectedPrefix = expectedContentTypePrefix.trim().toLowerCase();

      if (!normalized.startsWith(expectedPrefix)) {
        throw new Error(`Unexpected Content-Type for downloaded file (${contentType}).`);
      }
    }

    const contentLengthRaw = response.headers.get("Content-Length");
    if (contentLengthRaw && /^\d+$/.test(contentLengthRaw)) {
      const contentLength = Number.parseInt(contentLengthRaw, 10);
      if (Number.isFinite(contentLength) && contentLength > maxBytes) {
        throw new Error(`Downloaded file exceeded max size (${contentLength} > ${maxBytes}).`);
      }
    }

    const bytes = await readResponseBytesWithLimit({ response, maxBytes });

    if (validatePdfHeader && !isPdfHeader(bytes)) {
      throw new Error("Downloaded file does not appear to be a PDF.");
    }

    return { contentType, bytes };
  } finally {
    clearTimeout(timeoutId);
  }
}

export function fuzzySearch<T>(items: T[], key: string, query: string): FuseResult<T>[] {
  const fuse = new Fuse(items, {
    keys: [key],
  });

  return fuse.search(query);
}

/**
 * Returns a promise that resolves after a specified time in milliseconds.
 * Safe to use in React Server Components.
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generates a random string with specified character set.
 * @param length Length of the string to generate (default: 6)
 * @param options Configuration options for string generation
 * @returns Random string of specified length and character set
 */
export function generateRandomString(length: number = 6): string {
  const characters = "abcdefghijklmnopqrstuvwxyz0123456789";

  return Array.from({ length }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length)),
  ).join("");
}

/**
 * Converts a camel case string to a title case string.
 *
 * @param str The camel case string to convert.
 * @returns The title case string.
 */
export function camelCaseToTitleCase(str: string) {
  return str
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Waits for a tick of the event loop.
 * Safe to use in React Server Components.
 */
export function waitATick() {
  return new Promise((resolve) => setTimeout(resolve, 1));
}

// Function to format the error message
export function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object" && error !== null) {
    try {
      // Attempt to stringify the object for a more detailed message
      return JSON.stringify(error, null, 2);
    } catch (stringifyError) {
      // Fallback if stringify fails (e.g., circular references)
      return String(error);
    }
  }
  // Fallback for non-object, non-Error types
  return String(error);
}

export function promiseAll<T>(
  tasks: Array<Promise<T> | (() => Promise<T>)>,
  concurrency: number = 2,
): Promise<T[]> {
  const limit = pLimit(concurrency);
  const taskFactories = tasks.map((task) => (typeof task === "function" ? task : () => task));
  return Promise.all(taskFactories.map((task) => limit(() => task())));
}

/**
 * `promiseAll` with `Promise.allSettled` semantics: every task runs, at most `concurrency` at a
 * time, and one rejection never stops the others. Results keep the order of `tasks`.
 */
export function promiseAllSettled<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number = 2,
): Promise<PromiseSettledResult<T>[]> {
  const limit = pLimit(concurrency);
  return Promise.allSettled(tasks.map((task) => limit(task)));
}

/**
 * Utility function to capture, log and notify the user about errors.
 *
 * @param message - The error message.
 * @param params.error - The error instance.
 * @param params.logger - The logger instance.
 * @param params.notifyUser - Whether to notify the user.
 * @param params.title - Optional title for the user notification.
 * @param params.context - The context to log.
 * @param params.autoClose - The time in milliseconds to auto-close the notification.
 */
export function notifyError(
  message: string,
  {
    error,
    logger,
    title = "Error",
    notifyUser = true,
    context = {},
    autoClose = 5000,
  }: {
    error?: any;
    logger?: ReturnType<typeof createLogger>;
    notifyUser?: boolean;
    context?: Record<string, any>;
    autoClose?: number;
    title?: string;
  } = {},
) {
  // Prefer the structured logger path (BetterStack + Sentry.logger). When no logger is
  // supplied we skip the console.error fallback on purpose: Sentry's captureConsoleIntegration
  // would otherwise record a "[object Object]" event. Callers should pass `logger` — see
  // AGENTS.md "Never console.error(msg, obj)".
  logger?.error(message, {
    ...context,
    error: error,
    errorMessage: error?.message,
  });

  if (error instanceof Error) {
    Sentry.captureException(error, { extra: context });
  }

  if (notifyUser) {
    notifications.show({
      color: "red",
      title,
      message,
      autoClose,
    });
  }
}

/**
 * Deep equality check for objects and arrays.
 * More efficient than JSON.stringify and handles edge cases better.
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if values are deeply equal, false otherwise
 */
export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (typeof a === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
    }
    return true;
  }

  return false;
}

/**
 * Throttles the execution of the given functions and returns the results.
 *
 *  @example
 * const [provider, member] = await throttlePromises([
 *  () => Provider.getById(medplum, providerId),
 *  () => Member.getById(medplum, memberId),
 * ], { concurrency: 5 })
 *
 * @param functions - The list of functions to call.
 * @param config - The configuration for the throttle.
 */
export async function throttlePromises<T>(
  functions: Array<() => Promise<T>>,
  config?: { concurrency?: number; continueOnError?: false },
): Promise<T[]>;
export async function throttlePromises<T>(
  functions: Array<() => Promise<T>>,
  config: { concurrency?: number; continueOnError: true },
): Promise<(T | undefined)[]>;
export async function throttlePromises<T>(
  functions: Array<() => Promise<T>>,
  config: { concurrency?: number; continueOnError?: boolean } = {},
) {
  const { concurrency = 5, continueOnError = false } = config;

  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError("concurrency must be a positive integer");
  }

  const limit = pLimit(concurrency);

  if (!continueOnError) {
    return Promise.all(functions.map((fn) => limit(fn)));
  }

  // Swallow individual task errors and resolve to undefined so callers can filter
  const promises = functions.map((fn) => limit(fn).catch(() => undefined));
  return Promise.all(promises);
}

/**
 * Sleeps for a specified number of milliseconds.
 *
 * @param ms - The number of milliseconds to sleep.
 * @returns A promise that resolves after the specified number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface ZipCode {
  areaCode: string | undefined;
  extension: string | undefined;
}

/**
 * Parses a zip code into a area code and optional extension.
 *
 * @param code - The zip code to parse.
 * @returns The area code and extension.
 */
export function parseZipCode(code?: string): ZipCode {
  if (typeof code !== "string" || !code) {
    return { areaCode: undefined, extension: undefined };
  }

  // Trim spaces
  code = code.trim();

  // Match area code (5 digits), optionally an extension (4 digits)
  const match = code.match(/^(\d{5})(?:-(\d{4}))?$/);

  if (!match) {
    return { areaCode: undefined, extension: undefined };
  }

  return {
    areaCode: match[1] || undefined,
    extension: match[2] || undefined,
  };
}
